import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Card, Alert, Spinner, Button } from 'react-bootstrap';
import { userService, tournamentService, authService } from '../../services/firebaseService';

const SimpleStripeCheckout = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Récupérer les paramètres de l'URL
  const queryParams = new URLSearchParams(location.search);
  const type = queryParams.get('type') || 'subscription';
  const plan = queryParams.get('plan');
  const tournamentId = queryParams.get('tournamentId');
  
  useEffect(() => {
    // Rediriger vers la page d'abonnements si les paramètres sont manquants
    if (type === 'subscription' && !plan) {
      navigate('/subscription');
      return;
    }
    
    if (type === 'tournament' && !tournamentId) {
      navigate('/tournaments');
      return;
    }
    
    // Vérifier si l'utilisateur est connecté
    const user = authService.getCurrentUser();
    if (!user) {
      navigate('/login');
      return;
    }
    
    setLoading(false);
  }, [type, plan, tournamentId, navigate]);

  // Simuler le processus de paiement
  const handleProcessPayment = async () => {
    try {
      setProcessingPayment(true);
      setError('');
      
      // Simuler un délai pour le traitement du paiement
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      if (type === 'subscription') {
        // Pour un abonnement
        const sessionId = `sub_${Date.now()}`;
        await userService.updateSubscription(plan);
        
        // Rediriger vers le tableau de bord
        navigate('/', { 
          state: { 
            paymentSuccess: true, 
            message: `Abonnement ${getPlanName(plan)} activé avec succès!` 
          } 
        });
      } else if (type === 'tournament') {
        // Pour un paiement de tournoi
        const sessionId = `tour_${Date.now()}`;
        
        // Mettre à jour le statut de paiement du tournoi
        await tournamentService.updateTournament(tournamentId, {
          isPaid: true,
          paymentSessionId: sessionId,
          paymentDate: new Date()
        });
        
        // Rediriger vers la page du tournoi
        navigate(`/tournaments/${tournamentId}`, { 
          state: { 
            paymentSuccess: true, 
            message: 'Paiement pour le tournoi effectué avec succès!' 
          } 
        });
      }
    } catch (err) {
      console.error('Erreur lors du traitement du paiement:', err);
      setError(err.message || 'Une erreur est survenue lors du traitement du paiement. Veuillez réessayer.');
      setProcessingPayment(false);
    }
  };
  
  // Obtenir le nom du plan à partir de son ID
  const getPlanName = (planId) => {
    switch (planId) {
      case 'basic':
        return 'Basic';
      case 'premium':
        return 'Premium';
      case 'unlimited':
        return 'Unlimited';
      default:
        return 'Gratuit';
    }
  };
  
  // Obtenir le prix du plan à partir de son ID
  const getPlanPrice = (planId) => {
    switch (planId) {
      case 'basic':
        return 9.99;
      case 'premium':
        return 19.99;
      case 'unlimited':
        return 29.99;
      default:
        return 0;
    }
  };

  return (
    <Container className="my-5">
      <div className="row justify-content-center">
        <div className="col-md-6">
          <Card className="shadow">
            <Card.Header className="bg-primary text-white">
              <h4 className="mb-0">
                {type === 'subscription' 
                  ? `Abonnement ${getPlanName(plan)}` 
                  : 'Paiement pour un tournoi'}
              </h4>
            </Card.Header>
            <Card.Body className="p-4">
              {error && (
                <Alert variant="danger">{error}</Alert>
              )}
              
              {loading ? (
                <div className="text-center my-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Chargement...</span>
                  </Spinner>
                  <p className="mt-3">Chargement des informations de paiement...</p>
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <h5>Récapitulatif</h5>
                    {type === 'subscription' ? (
                      <div>
                        <p><strong>Plan :</strong> {getPlanName(plan)}</p>
                        <p><strong>Prix :</strong> {getPlanPrice(plan)}€ / mois</p>
                        <p><strong>Durée :</strong> Mensuel (renouvellement automatique)</p>
                      </div>
                    ) : (
                      <div>
                        <p><strong>Type :</strong> Paiement pour un tournoi</p>
                        <p><strong>ID du tournoi :</strong> {tournamentId}</p>
                        <p><strong>Prix :</strong> 5.00€</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="mb-4">
                    <h5>Informations de paiement</h5>
                    <div className="mb-3">
                      <label className="form-label">Numéro de carte</label>
                      <input 
                        type="text" 
                        className="form-control" 
                        placeholder="4242 4242 4242 4242" 
                        disabled={processingPayment}
                      />
                      <small className="text-muted">Pour la démo, utilisez le numéro 4242 4242 4242 4242</small>
                    </div>
                    
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Date d'expiration</label>
                        <input 
                          type="text" 
                          className="form-control" 
                          placeholder="MM/AA" 
                          disabled={processingPayment}
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">CVC</label>
                        <input 
                          type="text" 
                          className="form-control" 
                          placeholder="123" 
                          disabled={processingPayment}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="d-grid gap-2">
                    <Button 
                      variant="primary" 
                      size="lg" 
                      onClick={handleProcessPayment}
                      disabled={processingPayment}
                    >
                      {processingPayment ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Traitement en cours...
                        </>
                      ) : (
                        `Payer ${type === 'subscription' ? getPlanPrice(plan) + '€' : '5.00€'}`
                      )}
                    </Button>
                    
                    <Button 
                      variant="outline-secondary" 
                      onClick={() => type === 'subscription' ? navigate('/subscription') : navigate('/tournaments')}
                      disabled={processingPayment}
                    >
                      Annuler
                    </Button>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>
    </Container>
  );
};

export default SimpleStripeCheckout;
